// Global ⌘K / Ctrl+K command palette.
//
// Sections: Chats (fuzzy title match), Pages, Actions, Entities (actors,
// families, techniques via POST /explore/search) and Help. Help replaces
// the removed Docs page: short "How do I…" answers shown inline, Enter
// opens the page the answer refers to.
//
// Mounted once inside SessionProvider (App.tsx). Opened by the shortcut or
// by openCommandPalette() — the header trigger lives outside the session
// provider, so it talks to the palette through a window event.
//
// a11y: role="dialog" + aria-modal; the input is a combobox driving a
// listbox through aria-activedescendant, so focus never leaves the input
// (Tab is trapped there). Focus returns to the previous element on close.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSessionContext } from '../contexts/SessionContext';
import { useTheme } from '../contexts/ThemeContext';
import { searchEntities, type ExploreSearchResult } from '../lib/exploreApi';
import { actorDetailPath, familyDetailPath, techniqueDetailPath, searchPath } from '../lib/paths';
import { fuzzyFilter } from '../lib/fuzzy';
import './CommandPalette.css';

const OPEN_EVENT = 'malpedia:open-command-palette';

/** Open the palette from anywhere (e.g. a header button). */
export function openCommandPalette(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/** "⌘K" on Apple platforms, "Ctrl K" elsewhere. */
export function paletteShortcutLabel(): string {
  const p = typeof navigator !== 'undefined' ? navigator.platform || navigator.userAgent : '';
  return /Mac|iPhone|iPad/i.test(p) ? '⌘K' : 'Ctrl K';
}

type Section = 'Chats' | 'Pages' | 'Actions' | 'Entities' | 'Help';
const SECTION_ORDER: Section[] = ['Chats', 'Entities', 'Pages', 'Actions', 'Help'];

interface PaletteItem {
  id: string;
  section: Section;
  label: string;
  /** Extra words the fuzzy matcher sees but the user doesn't. */
  keywords?: string;
  hint?: string;
  /** CSS colour for the leading dot. */
  dot?: string;
  /** Help only: answer shown inline while the item is active. */
  answer?: string;
  run: () => void;
}

interface HelpEntry {
  q: string;
  a: string;
  path: string;
  page: string;
  keywords: string;
}

const HELP: HelpEntry[] = [
  {
    q: 'How do I pivot from an IOC?',
    a: 'Search the IP, domain, hash or URL (press / anywhere, or ask about it in a chat) and open it. Its page lists the reports that mention it and the indicators that co-occur with it; click any linked entity to keep pivoting.',
    path: '/search',
    page: 'Search',
    keywords: 'indicator ip domain hash url pivot ioc',
  },
  {
    q: 'How do I read the retrieval card?',
    a: 'Each answer has a retrieval card: the plan (query type, detected entities and keywords), how much each search strategy contributed to the passages that were kept, and a step timeline. Expand a section for details and open a source to read the report.',
    path: '/chat',
    page: 'Chat',
    keywords: 'sources strategies plan answer citations retrieval',
  },
  {
    q: 'How do I use the Atlas?',
    a: 'The Atlas draws every family, actor, IOC, technique, CVE and ASN from the graph on one map, rebuilt nightly. Toggle types, colour by cluster or type, search for a name and click a point to see its details.',
    path: '/overview',
    page: 'Atlas',
    keywords: 'map overview landscape clusters atlas',
  },
  {
    q: 'How do I explore an entity as a graph?',
    a: 'Open Graph and search for an actor, family, CVE or indicator to seed it, then expand nodes to pull in their neighbours. Saved graphs appear in the sidebar on the Graph page.',
    path: '/graph',
    page: 'Graph',
    keywords: 'graph neighbours relationships expand nodes',
  },
  {
    q: 'How do I organise my chats?',
    a: 'Pin a chat from its ⋯ menu, drag chats onto a folder, and select several (checkbox or Shift-click) to move, export or delete them together. The filter above the list searches chat titles.',
    path: '/chat',
    page: 'Chat',
    keywords: 'pin folder move export delete filter chats organise organize',
  },
  {
    q: 'How do I see what is trending?',
    a: 'Trends charts how often families, actors and other entities appear in reports over time. Pick a tab for each view.',
    path: '/trends',
    page: 'Trends',
    keywords: 'trends rising activity over time charts',
  },
];

const PAGES: { label: string; path: string; keywords: string }[] = [
  { label: 'Home', path: '/', keywords: 'start landing' },
  { label: 'Chat', path: '/chat', keywords: 'ask question conversation' },
  { label: 'Graph', path: '/graph', keywords: 'network explorer' },
  { label: 'Atlas', path: '/overview', keywords: 'overview map landscape' },
  { label: 'Search', path: '/search', keywords: 'find entities iocs query filter' },
  { label: 'Explore', path: '/explore', keywords: 'browse categories top lists dashboard' },
  { label: 'Trends', path: '/trends', keywords: 'charts activity' },
  { label: 'Settings', path: '/settings', keywords: 'preferences model configuration' },
  { label: 'Profile', path: '/profile', keywords: 'account avatar tokens' },
];

const ENTITY_CATEGORIES = ['actor', 'family', 'technique'] as const;

const ENTITY_DOT: Record<string, string> = {
  actor: 'var(--entity-actor)',
  family: 'var(--entity-family)',
  technique: 'var(--entity-technique)',
};

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

function formatDay(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);

  // Global shortcut + programmatic open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  if (!open) return null;
  return <PaletteDialog onClose={() => setOpen(false)} />;
}

function PaletteDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { sessions, switchSession, createSession, createTemporarySession, openExportDialog } =
    useSessionContext();
  const { resolved, setMode } = useTheme();

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Remember and restore focus; focus the input on open.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    inputRef.current?.focus();
    return () => {
      const el = restoreFocusRef.current;
      if (el && document.contains(el)) el.focus();
    };
  }, []);

  const go = useCallback((fn: () => void) => {
    onClose();
    fn();
  }, [onClose]);

  // ── Entity search (debounced, three small category-scoped queries) ────
  const debounced = useDebounced(query.trim(), 220);
  const entityQuery = useQuery({
    queryKey: ['command-palette-entities', debounced],
    enabled: debounced.length >= 2,
    staleTime: 60_000,
    queryFn: async () => {
      const parts = await Promise.all(
        ENTITY_CATEGORIES.map((category) =>
          searchEntities({ query: debounced, category, limit: 4 }).catch(() => ({ results: [] as ExploreSearchResult[] })),
        ),
      );
      return parts.flatMap((p) => p.results);
    },
  });
  const entitiesPending = query.trim().length >= 2 && (debounced !== query.trim() || entityQuery.isFetching);

  // ── Items ─────────────────────────────────────────────────────────────
  const items: PaletteItem[] = useMemo(() => {
    const q = query.trim();
    const out: PaletteItem[] = [];

    // Chats: pinned first, then most recent.
    // Skip temporary chats and empty, never-sent drafts.
    const named = sessions.filter((s) => !s.temporary && (s.title || s.messages.length > 0 || s.serverChatId));
    const ordered = [...named].sort(
      (a, b) => Number(!!b.pinned) - Number(!!a.pinned) || b.updatedAt - a.updatedAt,
    );
    const chats = fuzzyFilter(ordered, q, (s) => s.title || 'New chat', q ? 6 : 5);
    for (const s of chats) {
      out.push({
        id: `chat:${s.id}`,
        section: 'Chats',
        label: s.title || 'New chat',
        hint: s.pinned ? 'pinned' : formatDay(s.updatedAt),
        dot: 'var(--atlas-accent-2)',
        run: () => go(() => {
          switchSession(s.id);
          navigate('/chat');
        }),
      });
    }

    // Entities.
    if (q.length >= 2 && debounced === q) {
      for (const r of entityQuery.data ?? []) {
        const attackId = r.extra?.attack_id ? String(r.extra.attack_id) : null;
        const path =
          r.category === 'actor'
            ? actorDetailPath(r.value)
            : r.category === 'family'
              ? familyDetailPath(r.value)
              : techniqueDetailPath(attackId ?? r.value);
        out.push({
          id: `entity:${r.category}:${r.value}`,
          section: 'Entities',
          label: r.value,
          hint: r.category === 'technique' && attackId && attackId !== r.value ? `${attackId} · technique` : r.category,
          dot: ENTITY_DOT[r.category] ?? 'var(--atlas-muted)',
          run: () => go(() => navigate(path)),
        });
      }
    }
    // Always offer the full search (IOCs, CVEs, filters like type:domain).
    if (q.length >= 2) {
      out.push({
        id: `search:${q}`,
        section: 'Entities',
        label: `Search everything for “${q}”`,
        keywords: q,
        hint: 'Search',
        dot: 'var(--atlas-accent)',
        run: () => go(() => navigate(searchPath(q))),
      });
    }

    // Pages.
    for (const p of fuzzyFilter(PAGES, q, (x) => `${x.label} ${x.keywords}`)) {
      out.push({
        id: `page:${p.path}`,
        section: 'Pages',
        label: p.label,
        hint: p.path,
        dot: 'var(--atlas-accent)',
        run: () => go(() => navigate(p.path)),
      });
    }

    // Actions.
    const actions: PaletteItem[] = [
      {
        id: 'action:new-chat',
        section: 'Actions',
        label: 'New chat',
        keywords: 'create start ask',
        run: () => go(() => {
          createSession();
          navigate('/chat');
        }),
      },
      {
        id: 'action:new-temp-chat',
        section: 'Actions',
        label: 'New temporary chat',
        keywords: 'private incognito not saved',
        hint: 'not saved',
        run: () => go(() => {
          createTemporarySession();
          navigate('/chat');
        }),
      },
      {
        id: 'action:theme',
        section: 'Actions',
        label: resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        keywords: 'toggle theme dark light mode appearance',
        run: () => go(() => setMode(resolved === 'dark' ? 'light' : 'dark')),
      },
      {
        id: 'action:export',
        section: 'Actions',
        label: 'Export chats…',
        keywords: 'download markdown backup',
        run: () => go(() => openExportDialog()),
      },
    ];
    for (const a of fuzzyFilter(actions, q, (x) => `${x.label} ${x.keywords ?? ''}`)) {
      out.push({ ...a, dot: a.dot ?? 'var(--atlas-muted)' });
    }

    // Help.
    for (const h of fuzzyFilter(HELP, q, (x) => `${x.q} ${x.keywords}`)) {
      out.push({
        id: `help:${h.q}`,
        section: 'Help',
        label: h.q,
        hint: h.page,
        dot: 'var(--atlas-muted)',
        answer: h.a,
        run: () => go(() => navigate(h.path)),
      });
    }

    // Stable section order.
    return SECTION_ORDER.flatMap((sec) => out.filter((i) => i.section === sec));
  }, [
    query, debounced, sessions, entityQuery.data, resolved, go, navigate,
    switchSession, createSession, createTemporarySession, setMode, openExportDialog,
  ]);

  // Keep the active index in range when results change.
  useEffect(() => {
    setActive((i) => (items.length === 0 ? 0 : Math.min(i, items.length - 1)));
  }, [items.length]);
  useEffect(() => {
    setActive(0);
  }, [query]);

  // Keep the active option visible.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, items]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (items.length) setActive((i) => (i + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (items.length) setActive((i) => (i - 1 + items.length) % items.length);
        break;
      case 'Home':
        if (e.ctrlKey || !query) { e.preventDefault(); setActive(0); }
        break;
      case 'End':
        if (e.ctrlKey || !query) { e.preventDefault(); setActive(Math.max(0, items.length - 1)); }
        break;
      case 'Enter':
        e.preventDefault();
        items[active]?.run();
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        onClose();
        break;
      case 'Tab':
        // Focus trap: the input is the only focusable element.
        e.preventDefault();
        break;
    }
  };

  const optionId = (i: number) => `cmdk-option-${i}`;
  const activeItem = items[active];

  // Group for rendering while keeping a flat index for keyboard nav.
  let flat = -1;
  const sections = SECTION_ORDER.map((sec) => ({
    sec,
    rows: items.filter((i) => i.section === sec),
  })).filter((g) => g.rows.length > 0 || (g.sec === 'Entities' && entitiesPending));

  return ReactDOM.createPortal(
    <div
      className="malpedia-cmdk-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="malpedia-cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
      >
        <div className="malpedia-cmdk-input-row">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
            <circle cx="7" cy="7" r="4.5" />
            <line x1="10.5" y1="10.5" x2="14" y2="14" />
          </svg>
          <input
            ref={inputRef}
            className="malpedia-cmdk-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats, actors, families, pages, help…"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-listbox"
            aria-autocomplete="list"
            aria-activedescendant={activeItem ? optionId(active) : undefined}
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="malpedia-cmdk-kbd">Esc</kbd>
        </div>

        <div
          ref={listRef}
          id="cmdk-listbox"
          className="malpedia-cmdk-list"
          role="listbox"
          aria-label="Results"
        >
          {sections.map(({ sec, rows }) => (
            <div key={sec} className="malpedia-cmdk-section" role="group" aria-labelledby={`cmdk-sec-${sec}`}>
              <div id={`cmdk-sec-${sec}`} className="malpedia-cmdk-section-title" role="presentation">
                {sec}
              </div>
              {rows.map((item) => {
                flat += 1;
                const i = flat;
                const isActive = i === active;
                return (
                  <div
                    key={item.id}
                    id={optionId(i)}
                    data-index={i}
                    role="option"
                    aria-selected={isActive}
                    aria-describedby={isActive && item.answer ? 'cmdk-help-answer' : undefined}
                    className={`malpedia-cmdk-option${isActive ? ' active' : ''}${item.answer ? ' help' : ''}`}
                    onMouseMove={() => { if (!isActive) setActive(i); }}
                    onMouseDown={(e) => e.preventDefault() /* keep focus in the input */}
                    onClick={() => item.run()}
                  >
                    <div className="malpedia-cmdk-option-main">
                      <span className="malpedia-cmdk-dot" style={{ background: item.dot }} aria-hidden="true" />
                      <span className="malpedia-cmdk-label">{item.label}</span>
                      {item.hint && <small className="malpedia-cmdk-hint">{item.hint}</small>}
                    </div>
                    {isActive && item.answer && (
                      <p id="cmdk-help-answer" className="malpedia-cmdk-answer">
                        {item.answer}
                        <span className="malpedia-cmdk-answer-link">Enter ↵ opens {item.hint}</span>
                      </p>
                    )}
                  </div>
                );
              })}
              {sec === 'Entities' && entitiesPending && rows.length === 0 && (
                <div className="malpedia-cmdk-status" role="presentation">Searching…</div>
              )}
            </div>
          ))}
          {sections.length === 0 && (
            <div className="malpedia-cmdk-status" role="presentation">
              Nothing matches “{query.trim()}”.
            </div>
          )}
        </div>

        <div className="malpedia-cmdk-footer" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
